import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { canCancelOrder, canRestoreOrder } from "@msuite/utils/stock";
import React from "react";
import { useOrder } from "@/hooks/use-order";
import { client } from "@/hono";
import type { OrderPanelBaseProps } from "./shared";

/**
 * Cancel an order, or restore a cancelled one — the same pair the orders table offers per row and
 * the native order screen offers as buttons.
 *
 * Both cascade to every suborder, so both are confirmed. Cancelling is offered only while the
 * order is `open` or `pending`: past that a picker has taken stock off shelves, the handler
 * refuses it with a 409, and the button is disabled rather than hidden so a picked order shows why
 * the action is unavailable instead of silently losing it.
 */
export function OrderMenu({ orderId }: OrderPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const cancelModal = useAsyncModal();
	const restoreModal = useAsyncModal();

	/** Query */
	const { order } = useOrder(orderId);

	/** Functions */
	/**
	 * Refetch everything the new state reaches. `patchOrderState` writes the state to the order
	 * *and* to each of its suborders, so the suborder queries are as stale as the order's own.
	 */
	async function invalidateOrder() {
		await qc.invalidateQueries({
			predicate: invalidate(["order", "orders", "suborder", "suborders"]),
		});
	}

	async function handleOnChangeOrderState(operation: "cancel" | "restore") {
		const isConfirmed = await (operation === "cancel" ? cancelModal : restoreModal).confirm();
		if (!isConfirmed) return;
		const response = await client.stock.orders[":orderId"][operation].$patch({
			param: { orderId },
		});
		await responseHandler(response);
		await invalidateOrder();
	}

	/** Guard */
	if (!order) return null;

	/** Render */
	return (
		<React.Fragment>
			{canRestoreOrder(order.state) ? (
				<Button
					variant="white"
					icon={<Icon name="ArchiveRestore" />}
					onClick={() => handleOnChangeOrderState("restore")}
				>
					{t("stock.restore_order")}
				</Button>
			) : (
				<Button
					variant="danger"
					icon={<Icon name="CircleX" />}
					isDisabled={!canCancelOrder(order.state)}
					onClick={() => handleOnChangeOrderState("cancel")}
				>
					{t("stock.cancel_order")}
				</Button>
			)}
			<ConfirmModal
				controller={cancelModal}
				title={t("stock.cancel_order")}
				message={t("stock.cancel_order_confirmation_message")}
			/>
			<ConfirmModal
				controller={restoreModal}
				title={t("stock.restore_order")}
				message={t("stock.restore_order_confirmation_message")}
				confirmVariant="primary"
			/>
		</React.Fragment>
	);
}
